'use client';

import { RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';
import MuiAccordion, { type AccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, { type AccordionSummaryProps } from '@mui/material/AccordionSummary';
import { type SxProps, styled } from '@mui/material/styles';
import { type ReactNode, type SyntheticEvent, useState } from 'react';

import { campaignManagerTheme } from '@campaign-manager/theme';

import Text from '../Text';

const Accordion = styled((props: AccordionProps & { borderless?: boolean }) => (
  <MuiAccordion
    disableGutters
    elevation={0}
    square
    {...props}
  />
))(({ theme, borderless }) => ({
  border: `1px solid ${theme.palette.divider}`,
  ...(borderless ? { borderLeft: 'none' } : {}),
  ...(borderless ? { borderRight: 'none' } : {}),

  '&.Mui-disabled': {
    backgroundColor: campaignManagerTheme.color.lightGrey,
  },

  '&:first-of-type': {
    borderTopLeftRadius: campaignManagerTheme.paperBorderRadius.default,
    borderTopRightRadius: campaignManagerTheme.paperBorderRadius.default,

    ...(borderless ? { borderTop: 'none' } : {}),

    '& .MuiAccordionSummary-root': {
      borderTopLeftRadius: campaignManagerTheme.paperBorderRadius.default,
      borderTopRightRadius: campaignManagerTheme.paperBorderRadius.default,
    },
  },

  '&:last-child': {
    borderBottomLeftRadius: campaignManagerTheme.paperBorderRadius.default,
    borderBottomRightRadius: campaignManagerTheme.paperBorderRadius.default,

    ...(borderless ? { borderBottom: 'none' } : {}),

    '& .MuiAccordionSummary-root': {
      borderBottomLeftRadius: campaignManagerTheme.paperBorderRadius.default,
      borderBottomRightRadius: campaignManagerTheme.paperBorderRadius.default,
    },
  },

  '&:not(:last-child)': {
    borderBottom: 0,
  },

  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(() => ({}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: 0,
}));

export interface RadioAccordionOption {
  id: string;
  label: string;
  description?: ReactNode;
  comp?: ReactNode;
  disabled?: boolean;
}

interface Props {
  options: RadioAccordionOption[];
  selectedId?: string;
  onChange?: (option: RadioAccordionOption) => void;
  accordionSx?: SxProps;
  borderless?: boolean;
}

export default function RadioAccordion({
  options,
  selectedId,
  onChange,
  accordionSx,
  borderless,
}: Props) {
  // A radio cannot be deselected: the open panel follows the selection and stays
  // open on a re-click. Without a selection the accordion keeps its own state so
  // a panel can still be opened.
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState<string | false>(false);
  const expanded = selectedId ?? uncontrolledExpanded;

  const handleChange = (panel: string) => (_: SyntheticEvent, isExpanded: boolean) => {
    if (!isExpanded) {
      return;
    }
    setUncontrolledExpanded(panel);
    const option = options.find((option) => option.id === panel);
    if (option) onChange?.(option);
  };

  return (
    <Box>
      {options.map((option) => (
        <Accordion
          key={option.id}
          expanded={expanded === option.id}
          onChange={handleChange(option.id)}
          sx={accordionSx}
          borderless={borderless}
          disabled={option.disabled}
        >
          <AccordionSummary
            expandIcon={expanded === option.id ? <RadioButtonChecked /> : <RadioButtonUnchecked />}
            disabled={option.disabled}
            role="menuitemradio"
            aria-label={option.label}
          >
            <Stack>
              <Text
                component="div"
                size="md"
                weight="medium"
                color="inherit"
              >
                {option.label}
              </Text>
              {option.description && (
                <Text
                  component="div"
                  size="sm"
                  color="grey"
                >
                  {option.description}
                </Text>
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>{option.comp}</AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
